import React, {useEffect, useState} from 'react';
import {generateAIVideosText} from '../generateAIVideosText';
import PageTitle from '../../../../uiToolkit/PageTitle';
import './styles.css';
import 'react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css';
import {
  FILE_PICK_STEP,
  IMAGE_GENERATION_STEP,
  MUTE_EXCLUDE_STEP,
  VIDEO_DURATION_STEP,
  VOICE_NARRATION_STEP,
  SUMMARY_STEP,
  GENERATE_MICROLESSON_STEP,
  AI_ASSISTANT_STEP,
} from '../config';
import MuteExcudeStep from '../components/MuteExcludeStep';
import VideoDuratonStep from '../components/VideoDurationStep';
import ImageGenerationStep from '../components/ImageGenerationStep';
import SummaryStep from '../components/SummarySteps';
import VoiceNarrationStep from '../components/VoiceNarrationStep';
import GenerationSteps from '../components/GenerationSteps';
import UploadFileStep from '../components/UploadFIleStep';
import HelpTip from '../components/HelpTip';
import {Col, Container, Row} from 'react-bootstrap';
import {
  DialectKey,
  Gender,
  GENERATED_VIDEO_SHORT,
  LANGUAGE_CONFIG,
  getDialectLabel,
  LanguageKey,
  VIDEO_IMAGE_STYLES,
  VIDEO_RESPONSE_TYPES,
  VOICE_TONE,
} from '../../../../data/models/AI/VideoGenerationOptions';
import {getSecondsFromTimeText} from '../../../../utils/getTimeTextFromSeconds';
import {parseStringArrayToInt} from '../../../../utils/parseStringArrayToInt';
import Button from '../../../../uiToolkit/Buttons/Button';
import {MediaFile} from '../../../../data/models/LibraryFile/MediaFile';
import {VideoGenerationConfig} from '../../../../data/models/AI/VideoGenerationConfig';
import {MyLibraryModal} from '../../../../modules/MyLibraryModal';
import {SelectOption} from '../../../../data/models/UI/SelectOption';
import CreateMicrolessonStep from '../components/GenerateMicrolessonStep';
import AiAssistantStep from '../components/AiAssistantStep';

const MAX_IGNORE_AUDIO_SLIDES_DURATION_SECONDS = 3600;

interface Props {
  onSave: (config: VideoGenerationConfig) => void;
  calculateEstimation: (
    fileId: string,
    duration: number,
    aiImageRatio: number,
    slidesToIgnore?: number[],
  ) => void;
  onCancel: () => void;
  isLoading: boolean;
  isEstimationLoading: boolean;
  estimatedCost: number;
  availableCredits: number;
  fileOrientation: string;
  fetchFileOrientation: (fileId: string) => void;
  onBrowseAudio: (onSelect: (file: MediaFile) => void) => void;
}

const GenerateAIVideosUI = React.memo(
  ({
    onSave,
    onCancel,
    isLoading,
    calculateEstimation,
    estimatedCost,
    availableCredits,
    fileOrientation,
    fetchFileOrientation,
    isEstimationLoading,
    onBrowseAudio,
  }: Props) => {
    const [ignoreAudioSlidesList, setIgnoreAudioSlidesList] = useState('');
    const [ignoreSlidesList, setIgnoreSlidesList] = useState('');
    const [ignoreAudioSlidesDuration, setIgnoreAudioSlidesDuration] =
      useState('00:00:00');
    const [narrationType, setNarrationType] = useState('1');
    const [ignorePhrasesList, setIgnorePhrasesList] = useState<string[]>([]);
    const [videoDuration, setVideoDuration] = useState<string>(
      GENERATED_VIDEO_SHORT.toString(),
    );
    const [contextFiles, setContextFiles] = useState<MediaFile[]>([]);
    const [sliderValue, setSliderValue] = useState(0);
    const [mediaType, setMediaType] = useState('1');
    const [file, setFile] = useState<MediaFile>();

    const [language, setLanguage] = useState<LanguageKey>('en');
    const [dialectOptions, setDialectOptions] = useState<SelectOption[]>([]);
    const [selectedDialect, setSelectedDialect] = useState<DialectKey>('');
    const [voiceGender, setVoiceGender] = useState<Gender>('female');

    const [resposeStyle, setResponseStyle] = useState<string>(
      VIDEO_RESPONSE_TYPES[0].key,
    );
    const [resposeTone, setResponseTone] = useState<string>(VOICE_TONE[0].key);
    const [videoImageStyle, setVideoImageStyle] = useState<string>();

    const [ignoreListIsValid, setIgnoreListIsValid] = useState(true);
    const [ignoreAudioListIsValid, setIgnoreAudioListIsValid] = useState(true);

    const [generateMicrolesson, setGenerateMicrolesson] = useState(false);
    const [microlessonTitle, setMicrolessonTitle] = useState('');
    const [includeBranching, setIncludeBranching] = useState(true);
    const [includeGrading, setIncludeGrading] = useState(true);
    const [passingScore, setPassingScore] = useState<number | undefined>(50);
    const [enableAckNode, setEnableAckNode] = useState(true);
    const [generateMicroreels, setGenerateMicroreels] = useState(true);

    const [addAiAssistant, setAddAiAssistant] = useState(false);
    const [assistantName, setAssistantName] = useState('');
    const [assistantDescription, setAssistantDescription] = useState('');
    const [assistantResponseStyle, setAssistantResponseStyle] =
      useState<string>('direct');
    const [assistantCommunicationTone, setAssistantCommunicationTone] =
      useState<string>('formal');

    const [enableBackgroundMusic, setEnableBackgroundMusic] = useState(false);
    const [backgroundMusicType, setBackgroundMusicType] = useState<
      string | undefined
    >();
    const [backgroundAudioFile, setBackgroundAudioFile] = useState<
      MediaFile | undefined
    >();

    const [step, setStep] = useState(FILE_PICK_STEP);

    useEffect(() => {
      const config = LANGUAGE_CONFIG[language];
      if (!config) return;
      const dialects = Object.entries(config.dialects);
      const dialectOptions: SelectOption[] = dialects.map(([dialectKey]) => ({
        key: dialectKey,
        value: getDialectLabel(language, dialectKey),
      }));
      setDialectOptions(dialectOptions);
      if (dialects.length > 0) {
        const defaultDialect = dialectOptions[0].key;
        setSelectedDialect(defaultDialect);
        const defaultGender = config.dialects[defaultDialect];
        setVoiceGender(defaultGender);
      } else {
        setSelectedDialect('');
        setVoiceGender('female');
      }
    }, [language]);

    useEffect(() => {
      const config = LANGUAGE_CONFIG[language];
      if (!config) return;
      const defaultGender = config.dialects[selectedDialect];
      if (defaultGender) {
        setVoiceGender(defaultGender);
      }
    }, [language, selectedDialect]);

    useEffect(() => {
      if (file) {
        fetchFileOrientation(file.id);
      }
    }, [file]);

    useEffect(() => {
      if (step === SUMMARY_STEP && file) {
        calculateEstimation(
          file.id,
          parseInt(videoDuration, 10),
          sliderValue,
          parseStringArrayToInt(ignoreSlidesList),
        );
      }
    }, [step]);

    const ignoreAudioSlidesDurationIsValid = () => {
      if (ignoreAudioSlidesList === '') {
        return true;
      }
      return (
        ignoreAudioSlidesDuration !== '00:00:00' &&
        getSecondsFromTimeText(ignoreAudioSlidesDuration) <=
          MAX_IGNORE_AUDIO_SLIDES_DURATION_SECONDS
      );
    };

    const onSaveForm = () => {
      if (file && ignoreAudioSlidesDurationIsValid()) {
        let generateImages = false;
        let generateVideos = false;
        if (sliderValue > 0) {
          switch (mediaType) {
            case '1':
              {
                generateImages = true;
              }
              break;
            case '2':
              {
                generateVideos = true;
              }
              break;
            case '3': {
              generateImages = true;
              generateVideos = true;
            }
          }
        }
        onSave({
          libraryMediaFileId: file.id,
          generateAiImages: generateImages,
          slideToArtifactRatio:
            sliderValue > 0 ? Math.abs(100 - sliderValue) : undefined,
          ignoreSlides: parseStringArrayToInt(ignoreSlidesList),
          ignoreAudioSlides: parseStringArrayToInt(ignoreAudioSlidesList),
          ignoreAudioSlidesDuration: getSecondsFromTimeText(
            ignoreAudioSlidesDuration,
          ),
          duration: parseInt(videoDuration || '0', 10),
          gender: voiceGender,
          language: language,
          dialect: selectedDialect,
          phrasesToIgnore: ignorePhrasesList,
          generateAiVideos: generateVideos,
          responseStyle: resposeStyle,
          communicationTone: resposeTone,
          imageStyle: videoImageStyle,
          additionalContextFiles: contextFiles.map(file => file.id),
          narrateSlidesAsItIs: narrationType === '2',
          generateMicrolesson: generateMicrolesson,
          microlessonTitle: microlessonTitle,
          addBranching: includeBranching,
          addAssessment: includeGrading,
          passingScore: passingScore,
          addAckNode: enableAckNode,
          generateMicroreels: generateMicroreels,
          addAiAssistant: addAiAssistant,
          assistantTitle: assistantName,
          assistantDescription: assistantDescription,
          assistantConversationalTone: assistantCommunicationTone,
          assistantDirectAnswer: assistantResponseStyle === 'direct',
          backgroundAudio:
            enableBackgroundMusic && !backgroundAudioFile
              ? backgroundMusicType
              : undefined,
          backgroundAudioFileId:
            enableBackgroundMusic && backgroundAudioFile
              ? backgroundAudioFile.id
              : undefined,
        });
      }
    };

    const onAudioIgnoreSlidesChange = (value: string, isValid: boolean) => {
      setIgnoreAudioSlidesList(value);
      setIgnoreAudioListIsValid(isValid);
    };

    const onIgnoreSlidesChange = (value: string, isValid: boolean) => {
      setIgnoreSlidesList(value);
      setIgnoreListIsValid(isValid);
    };

    const onUpdateAiImageValue = (value: number) => {
      if (value === 0) {
        setVideoImageStyle(undefined);
      } else if (sliderValue === 0 && value !== 0) {
        setVideoImageStyle('Cinematic Photo');
      }
      setSliderValue(value);
    };

    const onNextStep = () => {
      if (step !== SUMMARY_STEP) {
        // Skip AI Assistant step if microlesson is not being generated
        if (step === GENERATE_MICROLESSON_STEP && !generateMicrolesson) {
          // Reset AI assistant state when skipping
          setAddAiAssistant(false);
          setAssistantName('');
          setAssistantDescription('');
          setAssistantResponseStyle('direct');
          setAssistantCommunicationTone('formal');
          setStep(SUMMARY_STEP);
        } else {
          setStep(step + 1);
        }
      } else {
        onSaveForm();
      }
    };

    const onSelectFile = (selectedFile?: MediaFile) => {
      setFile(selectedFile);
      setIgnoreAudioSlidesList('');
      setIgnoreAudioSlidesList('');
      setIgnoreAudioSlidesDuration('00:00:00');
      setNarrationType('1');
      setIgnorePhrasesList([]);
      setVideoDuration(GENERATED_VIDEO_SHORT.toString());
      setContextFiles([]);
      setSliderValue(0);
      setMediaType('1');
      setVoiceGender('female');
      setLanguage('en');
      setSelectedDialect(dialectOptions[0]?.key);
      setResponseStyle(VIDEO_RESPONSE_TYPES[0].key);
      setResponseTone(VOICE_TONE[0].key);
      setVideoImageStyle(undefined);
      setIgnoreListIsValid(true);
      setIgnoreAudioListIsValid(true);
      setEnableBackgroundMusic(false);
      setBackgroundMusicType(undefined);
      setBackgroundAudioFile(undefined);
    };

    const onSkipStep = () => {
      if (step === MUTE_EXCLUDE_STEP) {
        setIgnoreAudioSlidesList('');
        setIgnoreSlidesList('');
        setIgnoreAudioListIsValid(true);
        setIgnoreListIsValid(true);
      }
      if (step === IMAGE_GENERATION_STEP) {
        setSliderValue(0);
      }
      if (step === GENERATE_MICROLESSON_STEP) {
        setGenerateMicrolesson(false);
        setMicrolessonTitle('');
        setIncludeBranching(true);
        setIncludeGrading(true);
        setPassingScore(undefined);
        setEnableAckNode(true);
        setGenerateMicroreels(true);
      }
      if (step === AI_ASSISTANT_STEP) {
        setAddAiAssistant(false);
        setAssistantName('');
        setAssistantDescription('');
        setAssistantResponseStyle('direct');
        setAssistantCommunicationTone('formal');
      }
      setStep(step + 1);
    };

    const canSkipCurrentStep = () => {
      if (step === GENERATE_MICROLESSON_STEP) {
        return false;
      }
      if (step === AI_ASSISTANT_STEP) {
        return false;
      }
      if (
        step !== FILE_PICK_STEP &&
        step !== VIDEO_DURATION_STEP &&
        step !== VOICE_NARRATION_STEP &&
        step !== SUMMARY_STEP
      ) {
        return true;
      }
      return false;
    };

    const getNextButtonTitleForStep = () => {
      if (step !== SUMMARY_STEP) {
        return generateAIVideosText.nextStep;
      } else {
        return generateAIVideosText.generateVideo;
      }
    };

    const onPreviousStep = () => {
      if (step !== 0) {
        // Skip AI Assistant step when going back if microlesson is not being generated
        if (step === SUMMARY_STEP && !generateMicrolesson) {
          setStep(GENERATE_MICROLESSON_STEP);
        } else {
          setStep(step - 1);
        }
      }
    };

    const currentStepIsValid = () => {
      switch (step) {
        case FILE_PICK_STEP:
          return file !== undefined;
        case MUTE_EXCLUDE_STEP: {
          if (
            ignoreAudioSlidesList !== '' &&
            getSecondsFromTimeText(ignoreAudioSlidesDuration) === 0
          ) {
            return false;
          }
          return (
            ignoreAudioListIsValid &&
            ignoreListIsValid &&
            ignoreAudioSlidesDurationIsValid()
          );
        }
        case VIDEO_DURATION_STEP:
          if (
            enableBackgroundMusic &&
            !backgroundMusicType &&
            !backgroundAudioFile
          ) {
            return false;
          }
          return videoDuration !== undefined;
        case IMAGE_GENERATION_STEP:
          return true;
        case VOICE_NARRATION_STEP:
          return true;
        case GENERATE_MICROLESSON_STEP:
          if (generateMicrolesson) {
            return microlessonTitle.trim() !== '';
          }
          return true;
        case AI_ASSISTANT_STEP:
          if (addAiAssistant) {
            return assistantName.trim() !== '';
          }
          return true;
        case SUMMARY_STEP:
          return estimatedCost <= availableCredits;
      }
    };

    const getContentForStep = () => {
      switch (step) {
        case FILE_PICK_STEP:
          return <UploadFileStep onFileSelected={onSelectFile} file={file} />;
        case MUTE_EXCLUDE_STEP:
          return (
            <MuteExcudeStep
              ignoreAudioSlidesDuration={ignoreAudioSlidesDuration}
              ignoreAudioSlidesList={ignoreAudioSlidesList}
              ignoreSlidesList={ignoreSlidesList}
              setIgnoreAudioSlidesDuration={setIgnoreAudioSlidesDuration}
              setIgnoreAudioSlidesList={onAudioIgnoreSlidesChange}
              setIgnoreSlidesList={onIgnoreSlidesChange}
              ignorePhrasesList={ignorePhrasesList}
              setIgnorePhrasesList={setIgnorePhrasesList}
            />
          );
        case VIDEO_DURATION_STEP:
          return (
            <VideoDuratonStep
              videoDuration={videoDuration}
              setVideoDuration={setVideoDuration}
              enableBackgroundMusic={enableBackgroundMusic}
              setEnableBackgroundMusic={setEnableBackgroundMusic}
              backgroundMusicType={backgroundMusicType}
              setBackgroundMusicType={setBackgroundMusicType}
              backgroundAudioFile={backgroundAudioFile}
              setBackgroundAudioFile={setBackgroundAudioFile}
              onBrowseAudio={() =>
                onBrowseAudio(file => setBackgroundAudioFile(file))
              }
            />
          );
        case IMAGE_GENERATION_STEP:
          return (
            <ImageGenerationStep
              fileOrientation={fileOrientation}
              setSliderValue={onUpdateAiImageValue}
              sliderValue={sliderValue}
              videoImageStyle={videoImageStyle || ''}
              setVideoImageStyle={setVideoImageStyle}
              mediaType={mediaType}
              setMediaType={setMediaType}
            />
          );
        case VOICE_NARRATION_STEP:
          return (
            <VoiceNarrationStep
              language={language}
              setLanguage={setLanguage}
              dialectOptions={dialectOptions}
              selectedDialect={selectedDialect}
              setSelectedDialect={setSelectedDialect}
              voiceGender={voiceGender}
              setVoiceGender={setVoiceGender}
              resposeStyle={resposeStyle}
              setResposeStyle={setResponseStyle}
              responseTone={resposeTone}
              setResponseTone={setResponseTone}
              narrationType={narrationType}
              setNarrationType={setNarrationType}
              onContextFilesSelected={setContextFiles}
              contextFiles={contextFiles}
            />
          );
        case GENERATE_MICROLESSON_STEP:
          return (
            <CreateMicrolessonStep
              generateMicrolesson={generateMicrolesson}
              setGenerateMicrolesson={setGenerateMicrolesson}
              microlessonTitle={microlessonTitle}
              setMicrolessonTitle={setMicrolessonTitle}
              includeBranching={includeBranching}
              setIncludeBranching={setIncludeBranching}
              includeGrading={includeGrading}
              setIncludeGrading={setIncludeGrading}
              passingScore={passingScore}
              setPassingScore={setPassingScore}
              setEnableAckNode={setEnableAckNode}
              generateMicroreels={generateMicroreels}
              setGenerateMicroreels={setGenerateMicroreels}
            />
          );
        case AI_ASSISTANT_STEP:
          return (
            <AiAssistantStep
              addAiAssistant={addAiAssistant}
              setAddAiAssistant={setAddAiAssistant}
              assistantName={assistantName}
              setAssistantName={setAssistantName}
              assistantDescription={assistantDescription}
              setAssistantDescription={setAssistantDescription}
              assistantResponseStyle={assistantResponseStyle}
              setAssistantResponseStyle={setAssistantResponseStyle}
              assistantCommunicationTone={assistantCommunicationTone}
              setAssistantCommunicationTone={setAssistantCommunicationTone}
            />
          );
        case SUMMARY_STEP:
          return (
            <SummaryStep
              filename={file?.fileName || ''}
              excludePhrases={
                ignorePhrasesList.length
                  ? ignorePhrasesList.reduce(
                      (acc, value) => (acc += ', ' + value),
                    )
                  : ''
              }
              muteDuration={ignoreAudioSlidesDuration}
              muteSlides={ignoreAudioSlidesList}
              excludeSlides={ignoreSlidesList}
              duration={videoDuration || '0'}
              generateImages={
                (mediaType === '1' || mediaType === '3') && sliderValue > 0
              }
              generateVideo={
                (mediaType === '2' || mediaType === '3') && sliderValue > 0
              }
              imageRatio={sliderValue}
              language={language}
              dialect={selectedDialect}
              voiceGender={voiceGender}
              isEstimationLoading={isEstimationLoading}
              estimatedCost={estimatedCost}
              availableCredits={availableCredits}
              responseStyle={resposeStyle}
              responseTone={resposeTone}
              imageStyle={
                VIDEO_IMAGE_STYLES.find(x => x.key == videoImageStyle)?.value
              }
              exactNarration={narrationType === '2'}
              generateteMicrolesson={generateMicrolesson}
              microlessonTitle={microlessonTitle}
              includeBranching={includeBranching}
              includeGrading={includeGrading}
              passingScore={passingScore}
              addAiAssistant={addAiAssistant}
              assistantName={assistantName}
              assistantDescription={assistantDescription}
              assistantResponseStyle={assistantResponseStyle}
              assistantCommunicationTone={assistantCommunicationTone}
              backgroundMusicType={
                enableBackgroundMusic ? backgroundMusicType : undefined
              }
              backgroundAudioFileName={
                enableBackgroundMusic
                  ? backgroundAudioFile?.fileName
                  : undefined
              }
            />
          );
        default:
          return <div />;
      }
    };

    return (
      <div className="AIGenerationContainer">
        <div className="GenerateAIVideosContainer">
          <PageTitle title={generateAIVideosText.title} />
          <Container>
            <Row className="Content">
              <Col lg={12} xl={{span: 10, offset: 1}}>
                <GenerationSteps activeStep={step} />
                <div className="Body">
                  <div className="Steps">
                    {getContentForStep()}
                    <div className="Footer">
                      {step === SUMMARY_STEP && (
                        <Button
                          title={generateAIVideosText.exit}
                          onClick={onCancel}
                          uiType="text"
                        />
                      )}
                      <Button
                        title={generateAIVideosText.previousStep}
                        onClick={onPreviousStep}
                        uiType="hollow"
                        className="SkipButton"
                        disabled={step === 0}
                      />
                      {step !== SUMMARY_STEP && (
                        <Button
                          title={generateAIVideosText.skipStep}
                          onClick={onSkipStep}
                          uiType="hollow"
                          className="SkipButton"
                          disabled={!canSkipCurrentStep()}
                        />
                      )}
                      <Button
                        title={getNextButtonTitleForStep()}
                        onClick={onNextStep}
                        uiType="action"
                        disabled={!currentStepIsValid() || isLoading}
                        isLoading={isLoading}
                      />
                    </div>
                  </div>
                  <HelpTip
                    step={step}
                    generateMicrolesson={generateMicrolesson}
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </div>
        <MyLibraryModal />
      </div>
    );
  },
);

export default GenerateAIVideosUI;
